import { useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { useGetZipCodeData } from '../../../../../hooks/listings/useGetZipCodeData'
import { selectLatestMortgageRates } from '../../../../../store/selectors/mortgageRate'
import { Box, CurrencyInput, Flex, FormControl, FormLabel } from '@homebotapp/hb-react-component-catalog'
import { ListingLoanInfo, BucketedMonthlyHomeExpenses } from '../../../../../api/gqlaxy/gql/generated/graphql'
import { EstimatedMonthlyPaymentBreakdownHoaFees } from './EstimatedMonthlyPaymentBreakdownHoaFees/EstimatedMonthlyPaymentBreakdownHoaFees'
import { EstimatedMonthlyPaymentBreakdownDonutChart } from './EstimatedMonthlyPaymentBreakdownDonutChart/EstimatedMonthlyPaymentBreakdownDonutChart'
import { EstimatedMonthlyPaymentBreakdownCustomerDM } from './EstimatedMonthlyPaymentBreakdownCustomerDM/EstimatedMonthlyPaymentBreakdownCustomerDM'
import { EstimatedMonthlyPaymentBreakdownPropertyTaxes } from './EstimatedMonthlyPaymentBreakdownPropertyTaxes/EstimatedMonthlyPaymentBreakdownPropertyTaxes'
import { EstimatedMonthlyPaymentBreakdownHomeInsurance } from './EstimatedMonthlyPaymentBreakdownHomeInsurance/EstimatedMonthlyPaymentBreakdownHomeInsurance'
import { HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT } from '../../../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { EstimatedMonthlyPaymentBreakdownMortgageInsurance } from './EstimatedMonthlyPaymentBreakdownMortgageInsurance/EstimatedMonthlyPaymentBreakdownMortgageInsurance'
import { EstimatedMonthlyPaymentBreakdownPrincipalAndInterest } from './EstimatedMonthlyPaymentBreakdownPrincipalAndInterest/EstimatedMonthlyPaymentBreakdownPrincipalAndInterest'
import { EstimatedMonthlyPaymentBreakdownNationalAvgDownPaymentCard } from './EstimatedMonthlyPaymentBreakdownNationalAvgDownPaymentCard/EstimatedMonthlyPaymentBreakdownNationalAvgDownPaymentCard'
import { EstimatedMonthlyPaymentBreakdownUtilities } from './EstimatedMonthlyPaymentBreakdownUtilities/EstimatedMonthlyPaymentBreakdownUtilities'
import { EstimatedMonthlyPaymentBreakdownBuyDownToggle } from './EstimatedMonthlyPaymentBreakdownBuyDownToggle/EstimatedMonthlyPaymentBreakdownBuyDownToggle'
import {
  BUY_DOWN_MAPPINGS,
  BUY_DOWN_TYPE,
  ONE_ZERO,
  TWO_ONE
} from './EstimatedMonthlyPaymentBreakdownBuyDownToggle/BuyDownType'
import { NO_CLEAR_BUY_DOWN_TYPE_FOUND } from './EstimatedMonthlyPaymentBreakdownBuyDownToggle/BuyDownType'
import {
  getCalculatedBuyDownType,
  getRate,
  calculateMonthlyPMI,
  calculateMonthlyPayment
} from '../Utilities/EstimatedMonthlyPaymentUtilities'
import {
  DEFAULT_CREDIT_SCORE,
  DEFAULT_LOAN_TERM,
  NATIONAL_AVG_DOWN_PAYMENT,
  NATIONAL_AVG_MONTHLY_HOME_INSURANCE,
  NATIONAL_AVG_MONTHLY_PROPERTY_TAX_RATE
} from '../Utilities/EstimatedMonthlyPaymentConstants'

export const MSG = defineMessages({
  homePrice: {
    id: 'EstimatedMonthlyPaymentBreakdown.homePrice',
    defaultMessage: 'Home Price'
  }
})

export interface EstimatedMonthlyPaymentBreakdownProps {
  address?: string
  assumableBucketedMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  assumableMortgageLoanInfo?: ListingLoanInfo | null
  assumableMortgageSecondLoanInfo?: ListingLoanInfo | null
  assumableToggle?: boolean
  bucketedMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  buyDownToggle: boolean
  concessionAmount?: number
  homePrice: number
  isCalculatorsPage?: boolean
  listingBuyDownType?: BUY_DOWN_TYPE
  listingHasBuyDown?: boolean
  listingId?: string
  listingZipCode?: string
  loanInfo?: ListingLoanInfo | null
  setAssumableToggle: (assumableToggle: boolean) => void
  setBuyDownToggle: (buyDownToggle: boolean) => void
  setHasUsedMortgageCalculator: (hasUsedMortgageCalculator: boolean) => void
}

export const EstimatedMonthlyPaymentBreakdown = ({
  address,
  assumableBucketedMonthlyHomeExpenses,
  assumableMortgageLoanInfo,
  assumableMortgageSecondLoanInfo,
  assumableToggle = false,
  bucketedMonthlyHomeExpenses,
  buyDownToggle,
  concessionAmount,
  homePrice: initialHomePrice,
  isCalculatorsPage,
  listingBuyDownType = NO_CLEAR_BUY_DOWN_TYPE_FOUND,
  listingHasBuyDown,
  listingId,
  listingZipCode,
  loanInfo,
  setAssumableToggle,
  setBuyDownToggle,
  setHasUsedMortgageCalculator
}: EstimatedMonthlyPaymentBreakdownProps) => {
  const intl = useIntl()
  const rates = useSelector(selectLatestMortgageRates)
  const selectedBucketedMonthlyHomeExpenses = assumableToggle
    ? assumableBucketedMonthlyHomeExpenses
    : bucketedMonthlyHomeExpenses
  const [loanTerm, setLoanTerm] = useState(DEFAULT_LOAN_TERM)
  const [homePrice, setHomePrice] = useState(initialHomePrice.toString())
  const [creditScore, setCreditScore] = useState(Number(localStorage.getItem('creditScore')) || DEFAULT_CREDIT_SCORE)
  const [rate, setRate] = useState(getRate(rates?.[loanTerm], creditScore).toFixed(3))
  const [zipCode, setZipCode] = useState(listingZipCode || '')
  const { data: zipCodeData } = useGetZipCodeData({ input: { zipCode, homePrice: Number(homePrice) } })
  const [homeInsurance, setHomeInsurance] = useState(
    zipCodeData?.zipCodeData?.monthlyHomeInsurance?.toFixed(2) ||
      selectedBucketedMonthlyHomeExpenses?.homeInsurance?.toFixed(2) ||
      NATIONAL_AVG_MONTHLY_HOME_INSURANCE.toFixed(2)
  )
  const [propertyTaxes, setPropertyTaxes] = useState(
    zipCodeData?.zipCodeData?.monthlyPropertyTaxes?.toFixed(2) ||
      selectedBucketedMonthlyHomeExpenses?.propertyTaxes?.toFixed(2) ||
      ((NATIONAL_AVG_MONTHLY_PROPERTY_TAX_RATE * Number(homePrice)) / 12).toFixed(2)
  )
  const [mortgageInsurance, setMortgageInsurance] = useState(
    selectedBucketedMonthlyHomeExpenses?.mortgageInsurance?.toFixed(2) || '0'
  )
  const [totalMonthlyHomeExpenses, setTotalMonthlyHomeExpenses] = useState(
    selectedBucketedMonthlyHomeExpenses?.totalMonthlyHomeExpenses?.toFixed(2) || '0'
  )
  const [principalAndInterest, setPrincipalAndInterest] = useState(
    selectedBucketedMonthlyHomeExpenses?.monthlyPrincipalAndInterest?.toFixed(2) || '0'
  )
  const [secondMortgagePrincipalAndInterest, setSecondMortgagePrincipalAndInterest] = useState(
    selectedBucketedMonthlyHomeExpenses?.secondMortgagePrincipalAndInterest?.toFixed(2) || '0'
  )

  const startingDownPaymentPercentage =
    localStorage.getItem('downPaymentPercentage') ||
    (
      localStorage.getItem('downPaymentCash') &&
      (Number(localStorage.getItem('downPaymentCash')) / Number(homePrice)) * 100
    )?.toFixed(2) ||
    (loanInfo?.downpayment && (loanInfo?.downpayment / Number(homePrice)) * 100)?.toFixed(2) ||
    `${NATIONAL_AVG_DOWN_PAYMENT * 100}`

  const [downPaymentPercentage, setDownPaymentPercentage] = useState(startingDownPaymentPercentage)
  const [downPaymentCash, setDownPaymentCash] = useState(
    localStorage.getItem('downPaymentCash') ||
      (
        localStorage.getItem('downPaymentPercentage') &&
        (Number(homePrice) * Number(localStorage.getItem('downPaymentPercentage'))) / 100
      )?.toFixed(2) ||
      loanInfo?.downpayment.toFixed(2) ||
      '0'
  )
  const [hoaFees, setHoaFees] = useState(selectedBucketedMonthlyHomeExpenses?.monthlyHoaFees?.toFixed(2) || '0')
  const assumedMortgagePrincipalAndInterest = assumableBucketedMonthlyHomeExpenses?.monthlyPrincipalAndInterest || 0
  const showAvgPayment = downPaymentPercentage === (NATIONAL_AVG_DOWN_PAYMENT * 100).toFixed(2)
  const [utilitiesGas, setUtilitiesGas] = useState(localStorage.getItem('utilitiesGas') || '')
  const [utilitiesOther, setUtilitiesOther] = useState(localStorage.getItem('utilitiesOther') || '')
  const [utilitiesTrash, setUtilitiesTrash] = useState(localStorage.getItem('utilitiesTrash') || '')
  const [utilitiesWater, setUtilitiesWater] = useState(localStorage.getItem('utilitiesWater') || '')
  const [utilitiesElectric, setUtilitiesElectric] = useState(localStorage.getItem('utilitiesElectric') || '')
  const [utilitiesInternet, setUtilitiesInternet] = useState(localStorage.getItem('utilitiesInternet') || '')
  const totalUtilities =
    Number(utilitiesGas) +
    Number(utilitiesOther) +
    Number(utilitiesTrash) +
    Number(utilitiesWater) +
    Number(utilitiesElectric) +
    Number(utilitiesInternet)

  const calculatedBuyDownType = getCalculatedBuyDownType(
    concessionAmount,
    Number(homePrice) - Number(downPaymentCash),
    listingBuyDownType
  )
  const [selectedBuyDownType, setSelectedBuyDownType] = useState<BUY_DOWN_TYPE>(
    calculatedBuyDownType !== NO_CLEAR_BUY_DOWN_TYPE_FOUND ? calculatedBuyDownType : TWO_ONE
  )

  const [year, setYear] = useState('1')

  useEffect(() => {
    if (rates) {
      setRate(getRate(rates?.[loanTerm], creditScore).toFixed(3))
    }
  }, [rates, loanTerm, creditScore])

  useEffect(() => {
    if (assumableToggle && assumableMortgageLoanInfo?.loanAmount) {
      if (assumableMortgageLoanInfo?.loanAmount + Number(downPaymentCash) > Number(homePrice)) {
        setDownPaymentCash((Number(homePrice) - assumableMortgageLoanInfo?.loanAmount).toString())
        setDownPaymentPercentage('100')
      } else {
        setDownPaymentPercentage(
          ((Number(downPaymentCash) / (Number(homePrice) - assumableMortgageLoanInfo?.loanAmount)) * 100).toFixed(2)
        )
      }
    } else {
      if (Number(downPaymentCash) > Number(homePrice)) {
        setDownPaymentCash(homePrice)
        setDownPaymentPercentage('100')
      } else if (Number(downPaymentPercentage).toFixed(0) === '100' && Number(downPaymentCash) < Number(homePrice)) {
        // handles percent down payment of 100% set by assumable toggle
        setDownPaymentPercentage(startingDownPaymentPercentage)
        setDownPaymentCash(((Number(homePrice) * Number(startingDownPaymentPercentage)) / 100).toFixed(2))
      }
    }
  }, [assumableToggle, assumableMortgageLoanInfo, homePrice, downPaymentCash])

  useEffect(() => {
    if (Number(downPaymentPercentage) >= 20) {
      setMortgageInsurance('0')
    } else {
      setMortgageInsurance(selectedBucketedMonthlyHomeExpenses?.mortgageInsurance?.toFixed(2) || '0')
    }
  }, [downPaymentPercentage])

  useEffect(() => {
    const monthlyPMI = calculateMonthlyPMI(Number(homePrice) - Number(downPaymentCash), Number(homePrice), creditScore)
    setMortgageInsurance(monthlyPMI.toFixed(2))
  }, [creditScore, homePrice, downPaymentCash])

  useEffect(() => {
    // add clause to get updated rate for selected buy down type
    const selectedRate = getRate(Number(rate), creditScore)
    const buyDownAdjustment = BUY_DOWN_MAPPINGS[selectedBuyDownType].find(buyDown => buyDown.year === year)?.rateDrop
    const adjustedRate = selectedRate - buyDownAdjustment
    const monthlyPrincipalAndInterest = calculateMonthlyPayment(
      Number(homePrice) - Number(downPaymentCash),
      buyDownToggle ? adjustedRate : selectedRate,
      loanTerm
    )

    setPrincipalAndInterest(monthlyPrincipalAndInterest.toFixed(3))

    let secondMortgagePrincipalAndInterest = '0'

    if (assumableToggle) {
      secondMortgagePrincipalAndInterest = calculateMonthlyPayment(
        Number(homePrice) - assumableMortgageLoanInfo?.loanAmount - Number(downPaymentCash),
        getRate(Number(rate), creditScore),
        loanTerm
      ).toFixed(3)

      setSecondMortgagePrincipalAndInterest(secondMortgagePrincipalAndInterest)
    }

    setTotalMonthlyHomeExpenses(
      (
        totalUtilities +
        Number(hoaFees) +
        Number(propertyTaxes) +
        Number(homeInsurance) +
        Number(mortgageInsurance) +
        (assumableToggle ? Number(assumedMortgagePrincipalAndInterest) : Number(monthlyPrincipalAndInterest)) +
        (assumableToggle ? Number(secondMortgagePrincipalAndInterest) : 0)
      ).toFixed(2)
    )
  }, [
    buyDownToggle,
    selectedBuyDownType,
    year,
    rate,
    hoaFees,
    loanTerm,
    creditScore,
    propertyTaxes,
    homeInsurance,
    totalUtilities,
    downPaymentCash,
    mortgageInsurance,
    secondMortgagePrincipalAndInterest,
    selectedBucketedMonthlyHomeExpenses
  ])

  useEffect(() => {
    setDownPaymentCash(((Number(homePrice) * Number(downPaymentPercentage)) / 100).toFixed(2))
  }, [homePrice])

  useEffect(() => {
    setHomePrice(initialHomePrice.toString())
  }, [initialHomePrice])

  useEffect(() => {
    setHoaFees(selectedBucketedMonthlyHomeExpenses?.monthlyHoaFees?.toFixed(2) || '0')
  }, [selectedBucketedMonthlyHomeExpenses?.monthlyHoaFees])

  useEffect(() => {
    if (!zipCode) {
      setPropertyTaxes(
        selectedBucketedMonthlyHomeExpenses?.propertyTaxes?.toFixed(2) ||
          ((NATIONAL_AVG_MONTHLY_PROPERTY_TAX_RATE * Number(homePrice)) / 12).toFixed(2)
      )
    }
  }, [selectedBucketedMonthlyHomeExpenses?.propertyTaxes, homePrice, zipCode])

  useEffect(() => {
    if (!zipCode) {
      setHomeInsurance(
        selectedBucketedMonthlyHomeExpenses?.homeInsurance?.toFixed(2) || NATIONAL_AVG_MONTHLY_HOME_INSURANCE.toFixed(2)
      )
    }
  }, [selectedBucketedMonthlyHomeExpenses?.homeInsurance, zipCode])

  useEffect(() => {
    if (Number(downPaymentPercentage) < 20) {
      setMortgageInsurance(selectedBucketedMonthlyHomeExpenses?.mortgageInsurance?.toFixed(2) || '0')
    }
  }, [selectedBucketedMonthlyHomeExpenses?.mortgageInsurance])

  useEffect(() => {
    if (zipCodeData?.zipCodeData?.monthlyHomeInsurance) {
      setHomeInsurance(zipCodeData?.zipCodeData?.monthlyHomeInsurance?.toFixed(2))
    }
    if (zipCodeData?.zipCodeData?.monthlyPropertyTaxes) {
      setPropertyTaxes(zipCodeData?.zipCodeData?.monthlyPropertyTaxes?.toFixed(2))
    }
  }, [zipCodeData])

  /**
   * CONDITIONAL LOGIC for setting buy down type and toggle:
   *
   * 1. if there is a buy down type from api, use it. ensure this only changes on user selection, not change of down payment
   * 2. if there is no concession amount or the buy down type is not set, set the buy down type to TWO_ONE but don't default toggle to true
   * 3. if there is a concession amount, calculate the buy down type and set the buy down type and toggle
   * 4. if the concession amount can't cover any buy down type, set the buy down type to TWO_ONE and toggle to true
   * 5. if down payment percentage changes and buy down type changes, set the buy down type and toggle
   */

  useEffect(() => {
    // Only run if buyDownType hasn't been set yet or if downPaymentCash changed
    if (listingBuyDownType !== NO_CLEAR_BUY_DOWN_TYPE_FOUND) {
      return
    }

    const calculatedBuyDownType = getCalculatedBuyDownType(
      concessionAmount,
      Number(homePrice) - Number(downPaymentCash),
      listingBuyDownType
    )

    /**  I want it to only update if
     *  no listing buy down type
     *  calculated buy down type is not NO_CLEAR_BUY_DOWN_TYPE_FOUND
     *  AND
     *  selected buy down type is not the same as the calculated buy down type
     */
    if (calculatedBuyDownType !== NO_CLEAR_BUY_DOWN_TYPE_FOUND && selectedBuyDownType !== calculatedBuyDownType) {
      setSelectedBuyDownType(calculatedBuyDownType)
      setBuyDownToggle(true)
    }
  }, [concessionAmount, downPaymentCash, homePrice])

  return (
    <>
      <EstimatedMonthlyPaymentBreakdownDonutChart
        hoaFees={hoaFees}
        utilities={totalUtilities}
        propertyTaxes={propertyTaxes}
        homeInsurance={homeInsurance}
        assumableToggle={assumableToggle}
        mortgageInsurance={mortgageInsurance}
        totalMonthlyHomeExpenses={totalMonthlyHomeExpenses}
        secondMortgagePrincipalAndInterest={secondMortgagePrincipalAndInterest}
        principalAndInterest={
          assumableToggle
            ? assumableBucketedMonthlyHomeExpenses?.monthlyPrincipalAndInterest?.toFixed(2) || '0'
            : principalAndInterest
        }
      />
      <Flex gap={2} mt={2} direction='column'>
        <FormControl>
          <FormLabel size='xs'>{intl.formatMessage(MSG.homePrice)}</FormLabel>
          <CurrencyInput
            size='sm'
            name='homePrice'
            decimalScale={2}
            value={homePrice}
            // @ts-ignore
            onChange={val => setHomePrice(val)}
            onFocus={() => setHasUsedMortgageCalculator(true)}
            tracking={{
              guid: '4iiFDVKmehCuuQdEZYYngt',
              ui_context: isCalculatorsPage
                ? 'Mortgage Calculator Page'
                : HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
              descriptor: 'Home price input field'
            }}
          />
        </FormControl>

        {/* Component for Buy Down Concession */}
        <EstimatedMonthlyPaymentBreakdownBuyDownToggle
          buyDownToggle={buyDownToggle}
          buyDownType={selectedBuyDownType}
          listingId={listingId}
          loanAmount={Number(homePrice) - Number(downPaymentCash)}
          loanTerm={loanTerm}
          rate={Number(rate)}
          setAssumableToggle={setAssumableToggle}
          setBuyDownToggle={setBuyDownToggle}
          setBuyDownType={setSelectedBuyDownType}
          setYear={setYear}
          selectedYear={year}
        />

        <Box
          height={showAvgPayment ? 'auto' : '0'}
          opacity={showAvgPayment ? '100' : '0'}
          transform={showAvgPayment ? 'scale3d(1,1,1)' : 'scale3d(.8,.8,-10)'}
          transition='transform .2s ease-in-out, height .2s ease-in-out, opacity .1s ease-in-out'
        >
          <EstimatedMonthlyPaymentBreakdownNationalAvgDownPaymentCard />
        </Box>
        <EstimatedMonthlyPaymentBreakdownPrincipalAndInterest
          rate={rate}
          setRate={setRate}
          loanTerm={loanTerm}
          homePrice={homePrice}
          setLoanTerm={setLoanTerm}
          creditScore={creditScore}
          setCreditScore={setCreditScore}
          assumableToggle={assumableToggle}
          downPaymentCash={downPaymentCash}
          isCalculatorsPage={isCalculatorsPage}
          setDownPaymentCash={setDownPaymentCash}
          principalAndInterest={principalAndInterest}
          downPaymentPercentage={downPaymentPercentage}
          setDownPaymentPercentage={setDownPaymentPercentage}
          assumableMortgageLoanInfo={assumableMortgageLoanInfo}
          setHasUsedMortgageCalculator={setHasUsedMortgageCalculator}
          assumableMortgageSecondLoanInfo={assumableMortgageSecondLoanInfo}
          secondMortgagePrincipalAndInterest={secondMortgagePrincipalAndInterest}
          assumableBucketedMonthlyHomeExpenses={assumableBucketedMonthlyHomeExpenses}
        />
        <EstimatedMonthlyPaymentBreakdownUtilities
          utilitiesGas={utilitiesGas}
          totalUtilities={totalUtilities}
          utilitiesTrash={utilitiesTrash}
          utilitiesWater={utilitiesWater}
          utilitiesOther={utilitiesOther}
          setUtilitiesGas={setUtilitiesGas}
          utilitiesElectric={utilitiesElectric}
          utilitiesInternet={utilitiesInternet}
          isCalculatorsPage={isCalculatorsPage}
          setUtilitiesTrash={setUtilitiesTrash}
          setUtilitiesWater={setUtilitiesWater}
          setUtilitiesOther={setUtilitiesOther}
          setUtilitiesInternet={setUtilitiesInternet}
          setUtilitiesElectric={setUtilitiesElectric}
          setHasUsedMortgageCalculator={setHasUsedMortgageCalculator}
        />
        <EstimatedMonthlyPaymentBreakdownHoaFees
          hoaFees={hoaFees}
          setHoaFees={setHoaFees}
          isCalculatorsPage={isCalculatorsPage}
          setHasUsedMortgageCalculator={setHasUsedMortgageCalculator}
        />
        <EstimatedMonthlyPaymentBreakdownPropertyTaxes
          zipCode={zipCode}
          homePrice={homePrice}
          setZipCode={setZipCode}
          propertyTaxes={propertyTaxes}
          setPropertyTaxes={setPropertyTaxes}
          isCalculatorsPage={isCalculatorsPage}
          setHasUsedMortgageCalculator={setHasUsedMortgageCalculator}
        />
        <EstimatedMonthlyPaymentBreakdownMortgageInsurance
          isCalculatorsPage={isCalculatorsPage}
          mortgageInsurance={mortgageInsurance}
          setMortgageInsurance={setMortgageInsurance}
          downPaymentPercentage={downPaymentPercentage}
          setHasUsedMortgageCalculator={setHasUsedMortgageCalculator}
        />
        <EstimatedMonthlyPaymentBreakdownHomeInsurance
          zipCode={zipCode}
          setZipCode={setZipCode}
          homeInsurance={homeInsurance}
          setHomeInsurance={setHomeInsurance}
          isCalculatorsPage={isCalculatorsPage}
          setHasUsedMortgageCalculator={setHasUsedMortgageCalculator}
        />
        {listingId && <EstimatedMonthlyPaymentBreakdownCustomerDM listingId={listingId} address={address} />}
      </Flex>
    </>
  )
}
